import type { Dispatch, Middleware, MiddlewareAPI } from "@reduxjs/toolkit";

import type { FilingDecideItem } from "../choices/wire";
import type { Invalidation } from "../contexts/invalidation";
import { capturePageState, onPageStateChange } from "../pageState/registry";
import type {
  CommandType,
  Commands,
  ContextRef,
  DeviceKind,
  ErrorCode,
  Frame,
  FrameOf,
  OutgoingPart,
  WireChoice,
  WireMessage,
} from "../protocol/protocol";
import { MAX_SUBSCRIPTIONS, PROTOCOL_VERSION } from "../protocol/protocol";
import type { SandboxHandIn } from "../sandbox/core/hostCalls";
import { sameCandidate, type Candidate, type CandidateWatcher } from "../sandbox/host/candidate";
import {
  agentActions,
  STOPPABLE_RUN,
  type AgentState,
  type OutgoingFile,
} from "../store/agentSlice";
import { subscribeFields, targetKey, type Target, type TargetKey } from "../store/targets";
import { BACKOFF, backoffDelay, closeAction, newCid, parseFrame } from "./connection";

type State = { agent: AgentState };
type Store = MiddlewareAPI<Dispatch, State>;

/** The part of a WebSocket the client uses, so tests can hand it a fake. */
export type SocketLike = Pick<WebSocket, "send" | "close" | "onopen" | "onmessage" | "onclose">;

/** The ticket endpoint answered 403: this user may not use the assistant. */
export class TicketRefused extends Error {}

/**
 * The card a press was made on (its `args_sha256`) and what the user changed on it: an approve
 * names its card even with no changes, so a redrawn card is never approved unseen.
 */
export interface CardChanges {
  overrides?: Record<string, unknown>;
  shown: string;
}

export interface CommandError {
  code: ErrorCode | "disconnected";
  message: string;
  fields?: Record<string, unknown>;
  /** A transient refusal (a code call that could not be queued): send it again, backing off. */
  retryable?: true;
}

/** A code cell's tool call or wait (CM3), as `toolCallCommand` builds it. */
export type SandboxToolCall =
  | { type: "sandbox.tool_call"; args: Commands["sandbox.tool_call"] }
  | { type: "sandbox.wait"; args: Commands["sandbox.wait"] };

export interface Timers {
  now(): number;
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export interface AgentClientDeps {
  /** A string while the signed-in user may use the assistant, null otherwise; a change restarts. */
  session(state: unknown): string | null;
  url(): string;
  openSocket(url: string): SocketLike;
  /** POST agent/ws-ticket/; throws TicketRefused on 403. */
  mintTicket(tabId: string): Promise<string>;
  /** GET agent/threads/<id>/messages/?before_seq= */
  fetchEarlier(
    threadId: number,
    beforeSeq: number
  ): Promise<{ messages: WireMessage[]; has_earlier: boolean }>;
  invalidation: Invalidation;
  timers: Timers;
  /** rAF in a visible tab; a short timeout in a hidden one, where rAF never fires. */
  scheduleFlush(flush: () => void): void;
  randomId(): string;
  random(): number;
  tabId(): string;
  deviceId(): string;
  isHidden(): boolean;
  /** The browser says it has no network (navigator.onLine false). */
  isOffline(): boolean;
  /** For the other tabs' dots; a coarse pointer reads as mobile. */
  deviceKind(): DeviceKind;
  /** Calls back on visibilitychange, online and offline; returns the unsubscribe. */
  onWake(callback: (reason: WakeReason) => void): () => void;
  /** This tab's sandbox candidate report (C4): sent with hello, and with presence once it settles. */
  candidate?: CandidateWatcher;
}

const LINGER_MS = 30_000;
const COMPOSER_DEBOUNCE_MS = 150;
const PRESENCE_THROTTLE_MS = 100;
/** How long a focused page's state settles before focus.set carries the change. */
const FOCUS_PAGE_STATE_DEBOUNCE_MS = 5_000;
const STALE_RUN_MS = 30_000;
const STALE_CHECK_MS = 5_000;
/** How long an `unavailable` send is retried with the same cid before it shows as failed. */
const SEND_RETRY_FOR_MS = 60_000;
/** A send on a tab the server refused a ticket: nothing reconnects it, so it fails at once. */
const REFUSED_SEND = { code: "forbidden", message: "The assistant isn't available." } as const;

export type WakeReason = "visible" | "online" | "offline";

type Waiter = { resolve(value: unknown): void; reject(error: CommandError): void };

/**
 * The agent socket: one per tab, owned by a Redux middleware. The UI talks to it through the
 * returned methods; frames come back as `framesReceived` batches the slice reduces.
 */
export function createAgentClient(deps: AgentClientDeps) {
  const { timers } = deps;
  let store: Store | null = null;
  let session: string | null = null;
  let socket: SocketLike | null = null;
  let open = false;
  let generation = 0;
  let attempt = 0;
  let reconnectTimer: unknown = null;
  let pingTimer: unknown = null;
  let staleTimer: unknown = null;
  let unsubscribeWake: (() => void) | null = null;
  /** Who hears `workspace.changed` (the sandbox host, WS3). */
  const workspaceListeners = new Set<(frame: FrameOf<"workspace.changed">) => void>();
  const reconnectListeners = new Set<() => void>();
  /** Who hears `sandbox.tool_result` (the code-call bridge, CM3). */
  const toolResultListeners = new Set<(frame: FrameOf<"sandbox.tool_result">) => void>();
  let queue: Frame[] = [];
  let flushScheduled = false;
  const waiters = new Map<string, Waiter>();
  const subscribeCids: Record<string, TargetKey> = {};
  const subscribed = new Set<TargetKey>();
  const lingers = new Map<TargetKey, unknown>();
  const composerTimers = new Map<number, unknown>();
  const filesInFlight = new Set<number>();
  let presenceTimer: unknown = null;
  // The server overwrites thread, caret and selection on every update: always send them all.
  let presence: Commands["presence.update"] = {};
  let presenceActive = false;
  let focus: { focused: boolean; contextRef: ContextRef | null } | null = null;
  let focusPageTimer: unknown = null;
  let unsubscribePageState: (() => void) | null = null;
  // The candidate report this socket last sent: presence carries it only when it differs.
  let sentExecutor: Candidate | null = null;
  // The watcher's report settled since the last presence frame: only then may presence carry it.
  let executorDue = false;
  const targets = new Map<TargetKey, Target>();

  const state = () => store!.getState().agent;
  const dispatch = (action: Parameters<Dispatch>[0]) => store!.dispatch(action);

  function transmit<K extends CommandType>(type: K, fields: Commands[K], cid?: string): boolean {
    if (!socket || !open) return false;
    socket.send(JSON.stringify({ v: PROTOCOL_VERSION, type, ...fields, ...(cid && { cid }) }));
    return true;
  }

  /** A command answered by its cid: ack (or snapshot, pong) resolves, error rejects. */
  function command<K extends CommandType>(
    type: K,
    fields: Commands[K],
    cid = newCid(deps.randomId())
  ): Promise<unknown> {
    return new Promise((resolve, reject) => {
      if (!transmit(type, fields, cid)) {
        reject({ code: "disconnected", message: "Not connected." } satisfies CommandError);
        return;
      }
      waiters.set(cid, { resolve, reject });
    });
  }

  // ── Connection ────────────────────────────────────────────────────────────

  async function connect() {
    const mine = ++generation;
    reconnectTimer = null;
    dispatch(agentActions.connectionChanged("connecting"));
    let ticket: string;
    try {
      ticket = await deps.mintTicket(state().connection.tabId);
    } catch (error) {
      if (mine !== generation) return;
      if (error instanceof TicketRefused) {
        unavailable();
      } else {
        scheduleReconnect();
      }
      return;
    }
    if (mine !== generation) return;
    const next = deps.openSocket(deps.url());
    socket = next;
    next.onopen = () => {
      if (mine !== generation) return;
      open = true;
      const { tabId, deviceId } = state().connection;
      transmit("hello", {
        ticket,
        tab_id: tabId,
        device_id: deviceId,
        device_kind: deps.deviceKind(),
        ...executor(deps.candidate?.current(), true),
      });
    };
    next.onmessage = (event) => {
      if (mine !== generation) return;
      const frame = parseFrame(event.data);
      if (frame) receive(frame);
    };
    next.onclose = (event) => {
      if (mine !== generation) return;
      disconnected();
      const action = closeAction(event.code, event.reason);
      if (action === "banner") {
        dispatch(agentActions.connectionChanged("tabClosed"));
      } else if (action === "unavailable") {
        unavailable();
      } else {
        scheduleReconnect();
      }
    };
  }

  /** The assistant is not this user's (a refused ticket, or a close saying their access went). */
  function unavailable() {
    dispatch(agentActions.connectionChanged("unavailable"));
    // What waits to send fails, to be sent again or taken back into the composer.
    for (const message of Object.values(state().outbox)) {
      if (message.state !== "sending") continue;
      dispatch(agentActions.messageFailed({ cid: message.cid, ...REFUSED_SEND }));
    }
  }

  function disconnected() {
    socket = null;
    open = false;
    subscribed.clear();
    timers.clearTimeout(pingTimer);
    timers.clearTimeout(staleTimer);
    for (const waiter of waiters.values()) {
      waiter.reject({ code: "disconnected", message: "The connection dropped." });
    }
    waiters.clear();
  }

  function scheduleReconnect() {
    dispatch(agentActions.connectionChanged("backoff"));
    // wake() reconnects when the tab is visible again, or the network is back. Offline still
    // tries at the backoff cap: navigator.onLine can stay false, or no online event follow (a
    // network switch, a VPN adapter, a wake from sleep).
    if (deps.isHidden()) return;
    const delay = deps.isOffline() ? BACKOFF.maxMs : backoffDelay(attempt, deps.random);
    reconnectTimer = timers.setTimeout(() => void connect(), delay);
    attempt += 1;
  }

  function wake(reason: WakeReason) {
    const status = state().connection.status;
    if (reason === "offline") {
      // An offline browser can leave the socket open with nothing getting through: drop it, so
      // sends wait (and show it) until the next hello.ok sends them again. Until the network is
      // back, a reconnect waits for the backoff cap. A connect whose ticket is still minting gives
      // up too (the generation moves on), or it would open a socket offline.
      const dead = socket;
      if (!dead && status !== "connecting" && status !== "backoff") return;
      generation += 1;
      if (dead) {
        disconnected();
        dead.close();
      }
      timers.clearTimeout(reconnectTimer);
      scheduleReconnect();
    } else if (status === "backoff" || (status === "connecting" && reason === "online")) {
      // A try still under way (its ticket minting, or its handshake) went out on the dead network
      // and may hang: start afresh. connect() moves the generation on, so its late end is ignored.
      const stale = socket;
      if (stale) {
        disconnected();
        stale.close();
      }
      timers.clearTimeout(reconnectTimer);
      void connect();
    } else if (status === "open" && reason === "visible") {
      // A hidden tab may have missed frames: quietly re-load every thread on screen.
      dispatch(agentActions.resyncWanted(subscribedThreads()));
      afterBatch();
    }
  }

  function start() {
    dispatch(agentActions.identityAssigned({ tabId: deps.tabId(), deviceId: deps.deviceId() }));
    unsubscribeWake = deps.onWake(wake);
    unsubscribePageState = onPageStateChange(pageStateChanged);
    attempt = 0;
    void connect();
  }

  function stop() {
    flushPageState();
    generation += 1;
    timers.clearTimeout(reconnectTimer);
    unsubscribeWake?.();
    unsubscribeWake = null;
    unsubscribePageState?.();
    unsubscribePageState = null;
    focus = null;
    socket?.close(1000);
    disconnected();
    lingers.forEach((handle) => timers.clearTimeout(handle));
    lingers.clear();
    composerTimers.forEach((handle) => timers.clearTimeout(handle));
    composerTimers.clear();
    targets.clear();
    queue = [];
    dispatch(agentActions.agentReset());
  }

  /** focus.set, with what a focused page shows (`pageState/registry`). */
  function sendFocus() {
    if (!focus) return;
    const pageState = focus.focused ? capturePageState(focus.contextRef) : null;
    transmit("focus.set", {
      focused: focus.focused,
      context_ref: focus.contextRef,
      ...(pageState && { page_state: pageState }),
    });
  }

  function pageStateChanged() {
    if (!focus?.focused || !focus.contextRef) return;
    timers.clearTimeout(focusPageTimer);
    focusPageTimer = timers.setTimeout(() => {
      focusPageTimer = null;
      sendFocus();
    }, FOCUS_PAGE_STATE_DEBOUNCE_MS);
  }

  /** A slot change still waiting out its debounce goes now, before its page is left or blurred. */
  function flushPageState() {
    if (focusPageTimer === null) return;
    timers.clearTimeout(focusPageTimer);
    focusPageTimer = null;
    sendFocus();
  }

  function ping() {
    transmit("ping", {});
    pingTimer = timers.setTimeout(ping, state().connection.pingInterval * 1000);
  }

  function checkStaleRuns() {
    const now = timers.now();
    const quiet = Object.values(state().threads)
      .filter((thread) => thread.run?.status === "running" && thread.live)
      .filter((thread) => now - thread.live!.lastDeltaAt > STALE_RUN_MS)
      .map((thread) => thread.id);
    if (quiet.length) {
      dispatch(agentActions.resyncWanted(quiet));
      afterBatch();
    }
    staleTimer = timers.setTimeout(checkStaleRuns, STALE_CHECK_MS);
  }

  function helloAccepted(reconnect: boolean) {
    attempt = 0;
    timers.clearTimeout(pingTimer);
    pingTimer = timers.setTimeout(ping, state().connection.pingInterval * 1000);
    staleTimer = timers.setTimeout(checkStaleRuns, STALE_CHECK_MS);
    for (const key of Object.keys(state().targets)) subscribe(key);
    // A new socket is a new presence entry: tell the other tabs what this one shows.
    presenceTimer ??= timers.setTimeout(flushPresence, PRESENCE_THROTTLE_MS);
    for (const message of Object.values(state().outbox)) {
      if (message.state === "sending") void transmitSend(message.cid, timers.now());
    }
    if (reconnect) {
      deps.invalidation.reconnected();
      for (const listener of reconnectListeners) listener();
    }
  }

  // ── Frames in ─────────────────────────────────────────────────────────────

  function receive(frame: Frame) {
    const cid = "cid" in frame ? frame.cid : undefined;
    const waiter = cid ? waiters.get(cid) : undefined;
    if (frame.type === "error") {
      if (waiter) {
        waiters.delete(cid!);
        waiter.reject({
          code: frame.code,
          message: frame.message,
          fields: frame.fields,
          ...(frame.retryable && { retryable: true }),
        });
      } else if (frame.code === "not_found" && typeof frame.fields?.thread === "number") {
        dispatch(agentActions.targetLost({ threadId: frame.fields.thread }));
      }
      return;
    }
    if (waiter) {
      waiters.delete(cid!);
      waiter.resolve(frame.type === "ack" ? frame.result : frame);
    }
    if (frame.type === "ack" || frame.type === "pong") return;
    if (frame.type === "sandbox.tool_result") {
      for (const listener of toolResultListeners) listener(frame);
      return;
    }
    if (frame.type === "context.changed")
      return deps.invalidation.contextChanged(frame.context_ref);
    if (frame.type === "threads.changed") return deps.invalidation.threadsChanged();
    if (frame.type === "workspace.changed") {
      // The sandbox host's sync, then the Files panel's queries.
      for (const listener of workspaceListeners) listener(frame);
      return deps.invalidation.workspaceChanged(frame);
    }
    // A sandbox call that ended can move the thread to another workspace or write files: live,
    // stored, and in the history a subscribe sends (calls made while the thread was not open).
    if (frame.type === "tool.result") deps.invalidation.toolEnded(frame.name);
    if (frame.type === "message.appended") toolsEnded([frame.message]);
    if (frame.type === "snapshot") toolsEnded(frame.messages);
    if (frame.type === "hello.ok") {
      const reconnect = state().connection.everOpened;
      queue.push(frame);
      flush();
      helloAccepted(reconnect);
      return;
    }
    queue.push(frame);
    if (!flushScheduled) {
      flushScheduled = true;
      deps.scheduleFlush(flush);
    }
  }

  function toolsEnded(messages: WireMessage[]) {
    for (const message of messages) {
      for (const part of message.parts) {
        if (part.kind === "tool_result") deps.invalidation.toolEnded(part.name);
      }
    }
  }

  function flush() {
    flushScheduled = false;
    if (!queue.length) return;
    const frames = queue;
    queue = [];
    const snapshotCids = frames.flatMap((frame) =>
      frame.type === "snapshot" && frame.cid ? [frame.cid] : []
    );
    dispatch(
      agentActions.framesReceived({
        frames,
        subscribes: { ...subscribeCids },
        receivedAt: timers.now(),
      })
    );
    snapshotCids.forEach((cid) => delete subscribeCids[cid]);
    afterBatch();
  }

  /** Sends the subscribes the reducer asked for, and re-sends drafts a snapshot left dirty. */
  function afterBatch() {
    const wanted = Object.values(state().threads).filter((thread) => thread.resync === "wanted");
    if (wanted.length && open) {
      for (const thread of wanted) resubscribeThread(thread.id);
      dispatch(agentActions.resyncSent(wanted.map((thread) => thread.id)));
    }
    for (const thread of Object.values(state().threads)) {
      if (thread.composer.dirty && !composerTimers.has(thread.id)) scheduleComposer(thread.id);
      if (thread.composer.filesDirty && !filesInFlight.has(thread.id)) sendFiles(thread.id);
    }
  }

  function subscribedThreads(): number[] {
    const ids = [...subscribed].map((key) => state().targets[key]?.threadId);
    return [...new Set(ids.filter((id): id is number => id != null))];
  }

  // ── Subscriptions ─────────────────────────────────────────────────────────

  function subscribe(key: TargetKey) {
    const target = targets.get(key);
    if (!target || !open) return;
    const cid = newCid(deps.randomId());
    subscribeCids[cid] = key;
    subscribed.add(key);
    evictBeyondLimit();
    command("subscribe", subscribeFields(target), cid).catch((error: CommandError) => {
      delete subscribeCids[cid];
      if (error.code === "disconnected") return;
      subscribed.delete(key);
      const threadId = state().targets[key]?.threadId;
      if (threadId != null) dispatch(agentActions.targetLost({ threadId }));
    });
  }

  function resubscribeThread(threadId: number) {
    const key = Object.keys(state().targets).find(
      (candidate) => state().targets[candidate].threadId === threadId
    );
    if (key) subscribe(key);
  }

  /** At most 10 subscriptions per socket: drop the least recently viewed target nobody shows. */
  function evictBeyondLimit() {
    while (subscribed.size > MAX_SUBSCRIPTIONS) {
      const idle = [...subscribed]
        .map((key) => ({ key, target: state().targets[key] }))
        .filter(({ target }) => !target || target.refs === 0)
        .sort((a, b) => (a.target?.lastViewedAt ?? 0) - (b.target?.lastViewedAt ?? 0))[0];
      if (!idle) return;
      drop(idle.key);
    }
  }

  function drop(key: TargetKey) {
    timers.clearTimeout(lingers.get(key));
    lingers.delete(key);
    const threadId = state().targets[key]?.threadId;
    subscribed.delete(key);
    targets.delete(key);
    dispatch(agentActions.targetDropped(key));
    const stillShown = Object.values(state().targets).some(
      (target) => target.threadId === threadId
    );
    if (threadId != null && !stillShown) transmit("unsubscribe", { thread: threadId });
  }

  // ── Sending ───────────────────────────────────────────────────────────────

  async function transmitSend(cid: string, firstTriedAt: number): Promise<void> {
    const message = state().outbox[cid];
    if (!message) return;
    try {
      const result = (await command(
        "message.send",
        {
          thread: message.threadId,
          cid,
          parts: message.parts,
          ...(message.modelRef && { model_ref: message.modelRef }),
          ...(message.pageState && { page_state: message.pageState }),
        },
        cid
      )) as { seq: number };
      dispatch(agentActions.messageAcked({ cid, seq: result.seq }));
    } catch (raw) {
      const error = raw as CommandError;
      if (error.code === "disconnected") {
        // Re-sent with the same cid after hello.ok, which a refused tab never gets.
        if (state().connection.status === "unavailable") {
          dispatch(agentActions.messageFailed({ cid, ...REFUSED_SEND }));
        }
        return;
      }
      const retry = error.code === "unavailable" && timers.now() - firstTriedAt < SEND_RETRY_FOR_MS;
      if (retry) {
        timers.setTimeout(() => void transmitSend(cid, firstTriedAt), backoffDelay(2, deps.random));
        return;
      }
      dispatch(agentActions.messageFailed({ cid, code: error.code, message: error.message }));
    }
  }

  function sendComposer(threadId: number) {
    timers.clearTimeout(composerTimers.get(threadId));
    composerTimers.delete(threadId);
    const composer = state().threads[threadId]?.composer;
    if (!composer?.dirty) return;
    const text = composer.text;
    command("composer.update", { thread: threadId, text })
      .then((result) =>
        dispatch(
          agentActions.composerAcked({ threadId, text, rev: (result as { rev: number }).rev })
        )
      )
      .catch(() => {
        // Kept dirty: the next edit, snapshot or the pagehide beacon carries it.
      });
  }

  function scheduleComposer(threadId: number) {
    timers.clearTimeout(composerTimers.get(threadId));
    composerTimers.set(
      threadId,
      timers.setTimeout(() => sendComposer(threadId), COMPOSER_DEBOUNCE_MS)
    );
  }

  function editComposer(threadId: number, text: string) {
    dispatch(agentActions.composerEdited({ threadId, text }));
    scheduleComposer(threadId);
  }

  /** The draft's files go at once: a file is added or taken off in one step, never typed. */
  function sendFiles(threadId: number) {
    const composer = state().threads[threadId]?.composer;
    if (!composer?.filesDirty) return;
    const files = composer.files.map((file) => file.id);
    filesInFlight.add(threadId);
    command("composer.update", { thread: threadId, files }).then(
      (result) => {
        filesInFlight.delete(threadId);
        const rev = (result as { rev: number }).rev;
        dispatch(agentActions.composerAcked({ threadId, files, rev }));
        sendFiles(threadId); // an edit made while this one was in flight
      },
      (error: CommandError) => {
        filesInFlight.delete(threadId);
        if (error.code !== "invalid") return; // kept dirty: the next snapshot carries it
        // Refused (a file not on this thread): the snapshot shows what the draft holds, unless the
        // list changed meanwhile; that newer list is sent instead.
        dispatch(agentActions.composerFilesRefused({ threadId, files }));
        if (state().threads[threadId]?.composer.filesDirty) {
          sendFiles(threadId);
          return;
        }
        dispatch(agentActions.resyncWanted([threadId]));
        afterBatch();
      }
    );
  }

  function editComposerFiles(threadId: number, edit: (files: OutgoingFile[]) => OutgoingFile[]) {
    const composer = state().threads[threadId]?.composer;
    if (!composer) return;
    dispatch(agentActions.composerFilesEdited({ threadId, files: edit(composer.files) }));
    if (!filesInFlight.has(threadId)) sendFiles(threadId);
  }

  function attachFile(threadId: number, file: OutgoingFile) {
    editComposerFiles(threadId, (files) =>
      files.some((held) => held.id === file.id) ? files : [...files, file]
    );
  }

  function flushPresence() {
    presenceTimer = null;
    // The settled report only: one unsettled blip riding a caret flush would move the host.
    const report = executorDue ? executor(deps.candidate?.reported()) : {};
    executorDue = false;
    // Before any view has reported, only a changed candidate report is worth a frame.
    if (presence.thread === undefined && !("executor" in report)) return;
    transmit("presence.update", {
      ...presence,
      device_kind: deps.deviceKind(),
      active: presenceActive,
      ...report,
    });
    presenceActive = false;
  }

  /** The report as `executor`: with hello always, else only when changed (absent keeps it, C4). */
  function executor(report: Candidate | undefined, always = false): { executor?: Candidate } {
    if (!report || (!always && sameCandidate(report, sentExecutor))) return {};
    sentExecutor = report;
    return { executor: report };
  }

  deps.candidate?.onChange(() => {
    executorDue = true;
    presenceTimer ??= timers.setTimeout(flushPresence, PRESENCE_THROTTLE_MS);
  });

  // ── The middleware and the methods views call ─────────────────────────────

  const middleware: Middleware<object, State> = (api) => {
    store = api as Store;
    return (next) => (action) => {
      const result = next(action);
      const nextSession = deps.session(api.getState());
      if (nextSession !== session) {
        // Set first: stop() and start() dispatch, and those dispatches come back through here.
        const previous = session;
        session = nextSession;
        if (previous !== null) stop();
        if (session !== null) start();
      }
      return result;
    };
  };

  return {
    middleware,

    /** A view shows this target; subscribe if it is new. Pair with release. */
    retain(target: Target): TargetKey {
      const key = targetKey(target);
      timers.clearTimeout(lingers.get(key));
      lingers.delete(key);
      targets.set(key, target);
      dispatch(agentActions.targetRetained({ key, now: timers.now() }));
      if (!subscribed.has(key)) subscribe(key);
      return key;
    },

    release(target: Target) {
      const key = targetKey(target);
      dispatch(agentActions.targetReleased({ key, now: timers.now() }));
      if (state().targets[key]?.refs) return;
      lingers.set(
        key,
        timers.setTimeout(() => {
          lingers.delete(key);
          if (!state().targets[key]?.refs) drop(key);
        }, LINGER_MS)
      );
    },

    /**
     * Queues the message (shown at once as an outgoing row) and sends it; returns its cid. A file
     * with `pages` picked for it (the composer's `pagePicks`, by file id) carries them.
     */
    send(
      threadId: number,
      text: string,
      files: OutgoingFile[] = [],
      pages: Record<number, string> = {}
    ): string {
      const cid = newCid(deps.randomId());
      const parts: OutgoingPart[] = [
        ...(text ? [{ kind: "text" as const, text }] : []),
        ...files.map((file) =>
          pages[file.id]
            ? { kind: "file" as const, thread_file: file.id, pages: pages[file.id] }
            : { kind: "file" as const, thread_file: file.id }
        ),
      ];
      const modelRef = state().modelPicks[threadId] ?? null;
      const pageState = capturePageState(state().threads[threadId]?.contextRef ?? null);
      dispatch(
        agentActions.messageQueued({ cid, threadId, parts, text, files, modelRef, pageState })
      );
      void transmitSend(cid, timers.now());
      return cid;
    },

    /**
     * Resolves with the decided choice; a refusal re-loads the thread quietly and rejects. `items`
     * (a filing confirmation only) are the files left out and those whose type the user changed;
     * `rows` (a list card's approve only) the keys of the rows the user ticked; `changes` (an approval
     * card's press) the args_sha256 of the card shown (`shown`, on every press: a card redrawn
     * since is refused) and what the user changed on it (`overrides`).
     */
    async decide(
      ref: string,
      option: string,
      items?: FilingDecideItem[],
      rows?: string[],
      changes?: CardChanges
    ): Promise<WireChoice> {
      try {
        const fields = {
          ref,
          option,
          ...(items?.length ? { items } : {}),
          ...(rows ? { rows } : {}),
          ...(changes && { shown: changes.shown }),
          ...(changes?.overrides &&
            Object.keys(changes.overrides).length > 0 && { overrides: changes.overrides }),
        };
        const result = (await command("choice.decide", fields)) as {
          choice: WireChoice;
        };
        return result.choice;
      } catch (error) {
        const threadId = Object.values(state().threads).find((thread) => thread.choices[ref])?.id;
        if (threadId != null) {
          dispatch(agentActions.resyncWanted([threadId]));
          afterBatch();
        }
        throw error;
      }
    },

    async cancel(threadId: number): Promise<void> {
      const run = state().threads[threadId]?.run;
      if (run && STOPPABLE_RUN.includes(run.status)) await command("run.cancel", { run: run.id });
    },

    /**
     * A sandbox call's answer from this tab, which must hold the call's workspace lease (else it
     * rejects `forbidden`); `fired` is false when the call was answered already or timed out.
     */
    async handInSandbox(answer: SandboxHandIn): Promise<{ fired: boolean }> {
      const fields = {
        call: answer.call,
        result: answer.result ?? null,
        error: answer.error ?? null,
      };
      return (await command("sandbox.result", fields as SandboxHandIn)) as { fired: boolean };
    },

    /**
     * A code cell's tool call or wait (CM3): resolves on its ack (`{queued: true}`), and the answer
     * comes as a `sandbox.tool_result` frame (`onToolResult`). Rejects with the server's refusal,
     * or `disconnected`.
     */
    sendToolCall(call: SandboxToolCall): Promise<unknown> {
      return command(call.type, call.args);
    },

    /** Calls back on each `sandbox.tool_result` frame; returns the unsubscribe. */
    onToolResult(listener: (frame: FrameOf<"sandbox.tool_result">) => void): () => void {
      toolResultListeners.add(listener);
      return () => void toolResultListeners.delete(listener);
    },

    /** Calls back on each `workspace.changed` frame (a user's upload); returns the unsubscribe. */
    onWorkspaceChanged(listener: (frame: FrameOf<"workspace.changed">) => void): () => void {
      workspaceListeners.add(listener);
      return () => void workspaceListeners.delete(listener);
    },

    /** Calls back each time the socket is accepted again after a drop; returns the unsubscribe. */
    onReconnected(listener: () => void): () => void {
      reconnectListeners.add(listener);
      return () => void reconnectListeners.delete(listener);
    },

    editComposer,

    /** Puts a finished file on the thread's draft, in every tab. */
    attachFile,

    /** Takes a file off the thread's draft, in every tab (the link itself is the caller's). */
    detachFile(threadId: number, fileId: number) {
      editComposerFiles(threadId, (files) => files.filter((held) => held.id !== fileId));
    },

    /** Whether a message this tab has loaded carries the file (a sent file's link stays). */
    isFileSent(threadId: number, fileId: number): boolean {
      return (state().threads[threadId]?.messages ?? []).some((message) =>
        message.parts.some((part) => part.kind === "file" && part.thread_file_id === fileId)
      );
    },

    /** Sends every unsynced draft now, skipping the debounce (the tab is being hidden or left). */
    flushComposers() {
      for (const thread of Object.values(state().threads)) {
        if (thread.composer.dirty) sendComposer(thread.id);
      }
    },

    focusComposer(threadId: number, focused: boolean) {
      dispatch(agentActions.composerFocused({ threadId, focused }));
    },

    /** What this tab shows (thread, view) and its caret and selection; throttled. */
    reportPresence(update: Omit<Commands["presence.update"], "device_kind">) {
      const { active, ...fields } = update;
      presence = { ...presence, ...fields };
      presenceActive ||= !!active;
      presenceTimer ??= timers.setTimeout(flushPresence, PRESENCE_THROTTLE_MS);
    },

    reportFocus(focused: boolean, contextRef: ContextRef | null) {
      // A slot change still waiting: a blur or hide of its page sends it first; a focused report
      // carries the page as it is anyway, and a page left sent it as it went (`usePageContext`).
      if (!focused && focus?.contextRef === contextRef) flushPageState();
      timers.clearTimeout(focusPageTimer);
      focusPageTimer = null;
      focus = { focused, contextRef };
      sendFocus();
    },

    /** Sends a slot change still waiting out its debounce: the page is being left or closed. */
    flushPageState,

    closeTab(tabId: string): Promise<unknown> {
      return command("tab.close", { tab_id: tabId });
    },

    /** The Reconnect banner's button: the only way back after a 4410. */
    reconnect() {
      if (state().connection.status === "tabClosed") {
        attempt = 0;
        void connect();
      }
    },

    async loadEarlier(threadId: number): Promise<void> {
      const first = state().threads[threadId]?.messages[0];
      if (!first) return;
      const page = await deps.fetchEarlier(threadId, first.seq);
      dispatch(
        agentActions.earlierLoaded({
          threadId,
          messages: page.messages,
          hasEarlier: page.has_earlier,
        })
      );
    },

    /** Sends a failed message again, with its cid (a failed send is not remembered as sent). */
    retrySend(cid: string) {
      if (state().outbox[cid]?.state !== "failed") return;
      dispatch(agentActions.messageRetried(cid));
      void transmitSend(cid, timers.now());
    },

    /**
     * Puts a failed message back into its composer ("never a lost draft"): its text, and its files
     * on the draft again (they are already on the thread, so nothing uploads twice).
     */
    takeBack(cid: string) {
      const message = state().outbox[cid];
      if (message?.state !== "failed") return;
      const current = state().threads[message.threadId]?.composer.text ?? "";
      dispatch(agentActions.messageDiscarded(cid));
      if (message.text) {
        const text = current ? `${message.text}\n\n${current}` : message.text;
        editComposer(message.threadId, text);
      }
      message.files.forEach((file) => attachFile(message.threadId, file));
      // A PDF goes back with the pages it was sent with.
      for (const part of message.parts) {
        if (part.kind === "file" && part.pages) {
          const { threadId } = message;
          dispatch(
            agentActions.pagesPicked({ threadId, fileId: part.thread_file, pages: part.pages })
          );
        }
      }
    },
  };
}

export type AgentClient = ReturnType<typeof createAgentClient>;
